/**
 * optimise.ts — the `/optimise` contract (P1 US-108, FR-31, FR-33, FR-34, FR-35).
 *
 * This file closes the one explicit exclusion recorded in the P0 freeze notice. P0 could not
 * specify `/optimise` because the optimiser was not engine code: in the frozen oracle it is a UI
 * function (`optimiser(mod)`, lines 1084–1155) that reads checkboxes out of the DOM, sweeps the
 * catalog, and writes an HTML table. P0 froze what the engine computes; it deliberately froze
 * nothing about the sweep around it. **This file freezes the sweep.**
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT IS BEING SPECIFIED, AND WHAT IS BEING PRESERVED
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * Every rule below was read out of the oracle, not designed. The optimiser is a tool engineers
 * already use and already trust; P1 moves where it runs, not what it answers. Where the oracle does
 * something a contract would not have chosen — an empty family filter meaning "all families", no
 * tie-break on equal masses, a deflection rescale living in the UI — the contract records the
 * behaviour and names the line it came from, rather than quietly improving it. `OPTIMISE.md`
 * carries the same list in prose, for the reader who arrives at the documentation.
 *
 * The one thing this file DOES change is the shape of a bois candidate. The oracle emits a display
 * string, `"C24 63 x 200"`, and the "appliquer" button recovers the fields by
 * `nom.split(' ')` at indices 0, 1 and 3 — position-indexed parsing of a label. That is exactly the
 * kind of thing a contract exists to remove, so a bois candidate carries `classe`, `b` and `h` as
 * real fields **in addition to** `nom`. `nom` keeps its oracle spelling because it is what the
 * results table prints.
 *
 * RULES THIS FILE OBEYS (the same ones as the rest of the package)
 *  - No formulas. Nothing here computes an engineering value.
 *  - No import of `@ronceray/engine`. Asserted by `test/optimise.test.ts`, which scans every file
 *    in `src/` for an import form rather than for the string — several of them discuss the engine
 *    boundary at length in prose, and a guard that flags the word gets switched off.
 *  - Every field carries a `.describe()` with its unit (FR-28).
 *  - Names and enumerations are derived from `@ronceray/catalog`, never retyped.
 *
 * @see OPTIMISE.md — the ranking rule, the filters, the rescale, and the frozen sweep grids.
 */

import { CATALOG, CATALOG_VERSION } from '@ronceray/catalog';
import { z } from 'zod';

import {
  BoisRequestSchema,
  CreuxRequestSchema,
  LamineRequestSchema,
  PERSONNALISE_SECTION,
} from './request';
import { savedModuleSchema } from './saved-file';

/* ================================================================================================
 * Sweep grids — frozen, from the oracle
 * ============================================================================================== */

/**
 * The timber depth grid, oracle line 1081, verbatim and in oracle order. 24 values, 75–600 mm.
 *
 * Frozen as a contract constant rather than derived, because it is not catalog data: it is a list
 * of commercially available depths that somebody chose. There is nothing to derive it from, and a
 * "tidier" grid — round numbers only, or a regular step — would return different candidates and
 * therefore different answers.
 */
export const H_BOIS = [
  75, 100, 120, 125, 140, 150, 160, 175, 180, 200, 220, 225, 240, 250, 275, 300, 320, 350, 360, 400,
  450, 500, 550, 600,
] as const;

/** The timber width grid, oracle line 1082, verbatim and in oracle order. 13 values, 38–200 mm. */
export const B_BOIS = [38, 45, 50, 60, 63, 75, 80, 100, 120, 140, 160, 180, 200] as const;

/**
 * The number of (b, h) pairs one timber strength class contributes to a sweep: **281**.
 *
 * 13 × 24 = 312 combinations, less the 31 where `h < b`, which the oracle skips at line 1112 — a
 * section laid on its side is the same section, and sweeping both orientations would rank duplicate
 * geometry against itself. Asserted rather than asserted-by-comment in `test/optimise.test.ts`.
 *
 * The call counts that follow from it, and that the P0 spike measured, are one **more** than the
 * candidate counts, because every bois sweep runs one extra reference calculation first (oracle
 * line 1102) to obtain the stiffness the rescale is relative to:
 *
 * | mode       | classes | candidates | engine calls |
 * | ---------- | ------- | ---------- | ------------ |
 * | `actuelle` | 1       | 281        | 282          |
 * | `massif`   | 10 (C)  | 2 810      | 2 811        |
 * | `gl`       | 6 (GL)  | 1 686      | 1 687        |
 */
export const BOIS_PAIRS_PER_CLASSE = 281;

/* ================================================================================================
 * Sweep controls
 * ============================================================================================== */

/**
 * The seven rolled-profile designation families, derived exactly as the oracle derives them
 * (line 643: `famille(nom) = nom.split(' ')[0]`, collected in first-appearance order at line 645).
 *
 * Derived, not typed out, for the same reason `PROFIL_NAMES` is derived: a catalog that gained an
 * HEAA range would otherwise present a family the contract refuses to accept.
 */
export const FAMILLE_NAMES: readonly [string, ...string[]] = (() => {
  const seen: string[] = [];
  for (const profile of CATALOG.profils) {
    const famille = profile.nom.split(' ')[0];
    if (famille !== undefined && !seen.includes(famille)) seen.push(famille);
  }
  const [first, ...rest] = seen;
  if (first === undefined) {
    throw new Error(
      `@ronceray/contract: no profile families could be derived from the catalog, so no ` +
        `optimise request could ever validate (catalog version ${CATALOG_VERSION}).`,
    );
  }
  return [first, ...rest];
})();

/** One rolled-profile designation family: `IPE`, `IPN`, `HEA`, `HEB`, `HEM`, `UPE` or `UPN`. */
export const FamilleSchema = z
  .enum(FAMILLE_NAMES)
  .describe(
    'A rolled-profile designation family, i.e. the first space-delimited token of a profile name ' +
      '(oracle line 643). Dimensionless. One of: ' +
      FAMILLE_NAMES.join(', ') +
      '.',
  );

export type Famille = z.infer<typeof FamilleSchema>;

/**
 * The timber sweep scope, from the `#optbois` select (oracle markup lines 184–188).
 *
 * `actuelle` sweeps the section grid at the request's own strength class; `massif` sweeps it at
 * every solid-timber class (the ten whose name starts `C`); `gl` at every glulam class (the six
 * starting `GL`). The oracle partitions on the first character of the class name — `'C'` for
 * `massif`, `'G'` for anything else — at lines 1104–1106.
 */
export const OptimiseModeBoisSchema = z
  .enum(['actuelle', 'massif', 'gl'])
  .describe(
    "Timber sweep scope. Dimensionless. `actuelle` = the request's own strength class only " +
      '(281 candidates); `massif` = all 10 solid-timber C classes (2 810); `gl` = all 6 glulam GL ' +
      'classes (1 686). Drives the cost-unit weight: `actuelle` is charged 500, the two ' +
      'all-class scopes 3 000.',
  );

export type OptimiseModeBois = z.infer<typeof OptimiseModeBoisSchema>;

/* ================================================================================================
 * Request
 * ============================================================================================== */

/**
 * A `/optimise` request. Discriminated on `module`, because the three sweeps genuinely take
 * different controls: steel by family, hollow sections not at all, timber by strength-class scope.
 *
 * `input` is the module's **frozen calc request**, unchanged and un-widened. The sweep varies one
 * field of it — `profil`, `section`, or `(classe, b, h)` — and holds every other field, every load
 * and every safety factor, fixed. Reusing the calc schema rather than defining a looser "sweep
 * input" is what guarantees that a candidate the optimiser proposes is a request the calc endpoint
 * would also accept: the answer is applicable, not merely computable.
 */
export const OptimiseRequestSchema = z
  .discriminatedUnion('module', [
    z
      .object({
        module: z.literal('lamine'),
        input: LamineRequestSchema,
        familles: z
          .array(FamilleSchema)
          .default([])
          .describe(
            'Designation families to sweep. Dimensionless. **An empty array means NO FILTER, not ' +
              'no results** — the oracle guards the filter with `if (fams.length && ...)` at line ' +
              '1089, so unchecking every box sweeps all 133 profiles rather than none. That ' +
              'reading is inherited deliberately: the UI ships with all seven boxes checked, so ' +
              'the empty state is only reachable by a user clearing them, and answering "no ' +
              'sections found" to that would be a behaviour change. Duplicates are harmless; the ' +
              'filter is a membership test.',
          ),
      })
      .describe('Sweep the rolled-profile catalog, optionally restricted to some families.'),

    z
      .object({
        module: z.literal('creux'),
        input: CreuxRequestSchema,
      })
      .describe(
        'Sweep the hollow-section catalog. No controls: the oracle offers none, and the sweep ' +
          `covers all 144 catalogue sections — every row except \`${PERSONNALISE_SECTION}\`, ` +
          'which is skipped at line 1096 because it carries no geometry of its own. The custom ' +
          'geometry in the request `input` is therefore ignored by the sweep, as it is by the ' +
          'oracle.',
      ),

    z
      .object({
        module: z.literal('bois'),
        input: BoisRequestSchema,
        mode: OptimiseModeBoisSchema.default('actuelle'),
      })
      .describe('Sweep the timber section grid at one strength class or at a whole family.'),
  ])
  .describe(
    "Request for POST /v1/optimise. The `input` is the module's frozen calc request; the sweep " +
      'varies one field of it and holds the rest, so every candidate returned is a request the ' +
      'calc endpoint would accept unchanged.',
  );

export type OptimiseRequest = z.infer<typeof OptimiseRequestSchema>;
export type OptimiseRequestInput = z.input<typeof OptimiseRequestSchema>;

/* ================================================================================================
 * Candidates
 * ============================================================================================== */

/**
 * `elu` and `els` are engine outputs and are modelled the way `./response` models engine outputs:
 * a bare `number` check rather than `z.number()`, so a non-finite value is carried rather than
 * rejected. The contract does not get to call the frozen engine's output invalid.
 *
 * `els` is additionally nullable: `maxELS` is `engineNumberOrNull` in the response envelope, and
 * the oracle's `ok` test (`r.els.maxELS <= 1`) evaluates to `false` for `null`, so a candidate can
 * legitimately carry a null there and simply never pass.
 */
const engineNumber = z.custom<number>((value) => typeof value === 'number', {
  message: 'Expected a number produced by the engine (non-finite values are permitted).',
});

/**
 * One swept section, whatever the module. Five fields, and all five are the oracle's own — same
 * names, same values, same sources (lines 1092, 1099, 1119–1122).
 */
export const OptimiseCandidateSchema = z
  .object({
    nom: z
      .string()
      .min(1)
      .describe(
        'The section designation as displayed. Dimensionless. For steel it is the catalog name ' +
          '(`"IPE 300"`, `"SHS 100x100x5"`); for timber the oracle composes ' +
          '`classe + " " + b + " x " + h` (line 1120), e.g. `"C24 63 x 200"`. Timber candidates ' +
          'also carry the three parts as real fields — see `BoisOptimiseCandidateSchema`.',
      ),
    poids: engineNumber.describe(
      'Mass per unit length, kg/m. This is the ranking key. Source differs by module and is ' +
        'inherited as-is: `lamine` reads the catalog column `G` (line 1092), `creux` takes the ' +
        "engine's computed `v.G` (line 1099) because hollow-section properties are calculated " +
        "from h, b, t rather than tabulated, and `bois` takes the engine's `poids` (line 1121).",
    ),
    elu: engineNumber.describe(
      "Governing ultimate-limit-state utilisation for this candidate — the response envelope's " +
        '`max`. Dimensionless. ELU only; serviceability is the separate `els` field, exactly as ' +
        'in the calc envelope.',
    ),
    els: engineNumber
      .nullable()
      .describe(
        "Governing serviceability utilisation for this candidate — the response envelope's " +
          '`els.maxELS`. Dimensionless, nullable.',
      ),
    ok: z
      .boolean()
      .describe(
        "Whether this candidate satisfies both limit states: `elu <= 1 && els <= 1`, the oracle's " +
          'own test. Dimensionless. Only `ok` candidates are ranked or returned — see ' +
          '`OPTIMISE.md`. The field is kept in the payload anyway so the shape does not change ' +
          'meaning if a future scope ever returns the rejected ones too.',
      ),
  })
  .describe('One swept section and its two governing utilisations.');

export type OptimiseCandidate = z.infer<typeof OptimiseCandidateSchema>;

/**
 * A timber candidate: the common five fields plus the three that define the section.
 *
 * These three exist so that the UI's "appliquer" action can write `classe`, `b` and `h` back into
 * the form as values, instead of doing what the oracle does — `var p = nom.split(' ')` and then
 * `p[0]`, `p[1]`, `p[3]` (skipping the `"x"` at index 2). Position-indexed parsing of a display
 * label is a shape defined twice: once when the string is built, once when it is taken apart.
 */
export const BoisOptimiseCandidateSchema = OptimiseCandidateSchema.extend({
  classe: z
    .enum(CATALOG.bois.map((timberClass) => timberClass.classe) as [string, ...string[]])
    .describe('Timber strength class of this candidate, e.g. `"C24"`, `"GL28h"`. Dimensionless.'),
  b: z
    .number()
    .positive()
    .describe(`Section width of this candidate, mm. One of the ${B_BOIS.length} values of B_BOIS.`),
  h: z
    .number()
    .positive()
    .describe(
      `Section depth of this candidate, mm. One of the ${H_BOIS.length} values of H_BOIS, and ` +
        'never less than `b`.',
    ),
}).describe('One swept timber section, with its class and dimensions as fields rather than text.');

export type BoisOptimiseCandidate = z.infer<typeof BoisOptimiseCandidateSchema>;

/* ================================================================================================
 * Response
 * ============================================================================================== */

/**
 * How many candidates the response carries at most. The oracle shows `ok.slice(0, 10)` (line 1132).
 *
 * The cap is a display decision that the API now owns, so it is stated in the payload as
 * `truncatedTo` rather than left for a client to infer from `candidates.length` — which would be
 * ambiguous in the one case that matters, a sweep that found exactly ten.
 */
export const OPTIMISE_TOP_N = 10;

const optimiseEnvelopeFields = {
  engineVersion: z
    .string()
    .min(1)
    .describe(
      'Semver version of the calculation engine that swept, e.g. "1.0.0". Dimensionless. Same ' +
        'field, same meaning and same source as on the calc envelope: a ranking is only ' +
        'comparable to another ranking from the same engine.',
    ),
  catalogVersion: z
    .string()
    .min(1)
    .describe(
      'Version of the section / material catalog swept, e.g. "2026.08". Dimensionless. A catalog ' +
        'correction can change which section is lightest, so a stored ranking without this is ' +
        'not reproducible.',
    ),
  totalPassing: z
    .int()
    .nonnegative()
    .describe(
      'How many swept candidates satisfied BOTH limit states — the full count, before the top-N ' +
        'cut. Dimensionless. Both numbers are needed because the oracle prints them in different ' +
        'places: the table shows ten rows, the footer line under it says how many sections passed ' +
        'in total (line 1137). A UI that reported `candidates.length` there would tell an ' +
        'engineer that 10 sections work when 214 do.',
    ),
  truncatedTo: z
    .int()
    .positive()
    .describe(
      `The cap applied to \`candidates\`, always ${OPTIMISE_TOP_N} in v1. Dimensionless. Stated ` +
        'so a client can tell "the ten lightest of many" from "these are all of them" without ' +
        'guessing at a magic number.',
    ),
} as const;

/**
 * The `/optimise` response. Discriminated on `module` for one reason only: a timber candidate
 * carries three fields a steel candidate does not. Every other field is identical across the three
 * variants, and is defined once in `optimiseEnvelopeFields` above.
 */
export const OptimiseResponseSchema = z
  .discriminatedUnion('module', [
    z.object({
      ...optimiseEnvelopeFields,
      module: z.literal('lamine'),
      candidates: z
        .array(OptimiseCandidateSchema)
        .max(OPTIMISE_TOP_N)
        .describe(
          'The lightest passing profiles, ascending by `poids`, capped at `truncatedTo`. ' +
            'Ordering is part of the contract, not a convenience.',
        ),
    }),
    z.object({
      ...optimiseEnvelopeFields,
      module: z.literal('creux'),
      candidates: z
        .array(OptimiseCandidateSchema)
        .max(OPTIMISE_TOP_N)
        .describe(
          'The lightest passing hollow sections, ascending by `poids`, capped at `truncatedTo`.',
        ),
    }),
    z.object({
      ...optimiseEnvelopeFields,
      module: z.literal('bois'),
      candidates: z
        .array(BoisOptimiseCandidateSchema)
        .max(OPTIMISE_TOP_N)
        .describe(
          'The lightest passing timber sections, ascending by `poids`, capped at `truncatedTo`. ' +
            'Each carries `classe`, `b` and `h` as fields so "appliquer" need not parse `nom`.',
        ),
    }),
  ])
  .describe(
    'Response for POST /v1/optimise, HTTP 200. A sweep that found nothing passing is a ' +
      'successful sweep: `candidates: []`, `totalPassing: 0`, HTTP 200 — the same rule as a ' +
      'failing check on the calc endpoints (FR-25).',
  );

export type OptimiseResponse = z.infer<typeof OptimiseResponseSchema>;

/* ================================================================================================
 * Cost units
 * ============================================================================================== */

/**
 * Cost-unit weights for `/optimise`, from the P0 spike §7.2, where a calc is the reference unit 1.
 *
 * They are set from measured CPU, not from call counts: the timber sweeps cost far less per call
 * than the steel ones because a long sweep warms itself into V8's optimising tier, which is why
 * 2 811 timber calls are 3 000 units and 133 steel calls are 500 rather than 50.
 *
 * Defined here, in the contract, because the number a client is billed is part of what the API
 * promises — not an implementation detail of the quota middleware that happens to count it.
 */
export const OPTIMISE_COST_UNITS = {
  /** Any single-strength-class sweep: `lamine`, `creux`, or `bois` at `mode: 'actuelle'`. */
  singleClass: 500,
  /** A whole-family timber sweep: `bois` at `mode: 'massif'` or `mode: 'gl'`. */
  allClasses: 3000,
} as const;

/**
 * The cost units a given request will be charged. One function so the quota middleware, the docs
 * and any future pricing page read the same rule rather than three copies of an `if`.
 */
export function optimiseCostUnits(request: OptimiseRequest): number {
  if (request.module === 'bois' && request.mode !== 'actuelle') {
    return OPTIMISE_COST_UNITS.allClasses;
  }
  return OPTIMISE_COST_UNITS.singleClass;
}

/* ================================================================================================
 * Module list
 * ============================================================================================== */

/**
 * The modules `/optimise` accepts — the same three as `/calc`, reused rather than restated, for
 * the same reason every other list in this package is reused: a shape written twice is the bug.
 */
export const OPTIMISE_MODULES = savedModuleSchema.options;
